import api from '@/lib/api';
import type { Club, Court } from '@/types/club.model';
import {
  createApiWithFields,
  createSingleApiWithFields,
  HttpMethod,
} from '@/lib/api-base';
import { CLUB_FIELDS } from './clubs-fields.config';
import type { FieldDefinition } from '@/types/fields-api.model';

// Helper functions using the field configurations with different HTTP methods
const getClubsByOwnerWithFields = createApiWithFields<Club, typeof CLUB_FIELDS>(
  '/clubs/current-owner',
  CLUB_FIELDS,
  HttpMethod.GET,
);

const getClubsByManagerWithFields = createApiWithFields<
  Club,
  typeof CLUB_FIELDS
>('/clubs/current-manager', CLUB_FIELDS, HttpMethod.GET);

const getClubsByInstructorWithFields = createApiWithFields<
  Club,
  typeof CLUB_FIELDS
>('/clubs/current-instructor', CLUB_FIELDS, HttpMethod.GET);

const getClubsByStaffWithFields = createApiWithFields<
  Club,
  typeof CLUB_FIELDS
>('/clubs/current-staff', CLUB_FIELDS, HttpMethod.GET);

const createClubWithFields = createSingleApiWithFields<
  Club,
  typeof CLUB_FIELDS
>('/clubs', CLUB_FIELDS, HttpMethod.POST);

const getClubByIdWithFields = (clubId: string) =>
  createSingleApiWithFields<Club, typeof CLUB_FIELDS>(
    `/clubs/${clubId}`,
    CLUB_FIELDS,
    HttpMethod.GET,
  );

const updateClubWithFields = createSingleApiWithFields<
  Club,
  typeof CLUB_FIELDS
>('/clubs', CLUB_FIELDS, HttpMethod.PUT);

export const getClubsByOwner = async (
  fields: FieldDefinition = CLUB_FIELDS.DEFAULT,
): Promise<Club[]> => {
  return await getClubsByOwnerWithFields(fields);
};

export const getClubsByManager = async (
  fields: FieldDefinition = CLUB_FIELDS.DEFAULT,
): Promise<Club[]> => {
  return await getClubsByManagerWithFields(fields);
};

export const getClubsByInstructor = async (
  fields: FieldDefinition = CLUB_FIELDS.DEFAULT,
): Promise<Club[]> => {
  return await getClubsByInstructorWithFields(fields);
};

export const getClubsByStaff = async (
  fields: FieldDefinition = CLUB_FIELDS.TURNOS,
): Promise<Club[]> => {
  return await getClubsByStaffWithFields(fields);
};

export const getClubById = async (
  clubId: string,
  fields: FieldDefinition = CLUB_FIELDS.FULL,
): Promise<Club> => {
  const getClubByIdApi = getClubByIdWithFields(clubId);
  return await getClubByIdApi(fields);
};

export const createClub = async (
  club: Partial<Club>,
  fields: FieldDefinition = CLUB_FIELDS.FULL,
): Promise<Club> => {
  return await createClubWithFields(fields, club);
};

export const updateClub = async (
  club: Partial<Club>,
  fields: FieldDefinition = CLUB_FIELDS.FULL,
): Promise<Club> => {
  return await updateClubWithFields(fields, club);
};

export const createCourt = async (court: Court): Promise<Court> => {
  const response = await api.post('/courts/new-court', court);
  return response.data;
};

export const updateCourt = async (court: Court): Promise<Court> => {
  const response = await api.put('/courts/edit-court', court);
  return response.data;
};

export const deleteCourt = async (courtId: string): Promise<boolean> => {
  const response = await api.delete('/courts/court', {
    params: { uuid: courtId },
  });
  return response.data;
};

export const enableClub = async (clubId: string): Promise<boolean> => {
  const response = await api.patch(`/clubs/${clubId}/enable`);
  return response.data;
};

export const disableClub = async (clubId: string): Promise<boolean> => {
  const response = await api.patch(`/clubs/${clubId}/disable`);
  return response.data;
};

export const uploadImageForClub = async (file: File, format = 'BANNER') => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/images/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    params: {
      format,
    },
  });
  return response.data;
};

export const addManagerToClub = async (
  clubId: string,
  email: string,
): Promise<void> => {
  const response = await api.post(`/clubs/${clubId}/add-manager`, { email });
  return response.data;
};

export const removeManagerFromClub = async (
  clubId: string,
  email: string,
): Promise<void> => {
  const response = await api.post(`/clubs/${clubId}/remove-manager`, { email });
  return response.data;
};

export const addInstructorToClub = async (
  clubId: string,
  email: string,
): Promise<void> => {
  const response = await api.post(`/clubs/${clubId}/add-instructor`, { email });
  return response.data;
};

export const removeInstructorFromClub = async (
  clubId: string,
  email: string,
): Promise<void> => {
  const response = await api.post(`/clubs/${clubId}/remove-instructor`, {
    email,
  });
  return response.data;
};

export const transferClub = async (
  clubId: string,
  email: string,
): Promise<void> => {
  const response = await api.patch(`/clubs/${clubId}/transfer`, { email });
  return response.data;
};

export default api;
